/**
 * Build Taxonomy Governance Report
 * MOVED from src/developer/diagnostics/ (Phase J-1)
 */
import { TAXONOMY } from '../../../config/taxonomyRegistry'
import visualGovernanceMap from '../../../governance/data/VISUAL_GOVERNANCE_MAP.json'
import { getAllQuestionsWithSourcesForDiagnostics } from '../../../data/questionLoader'
import { VISUAL_AID_FACTORY_SUPPORTED_TYPES } from '../../../visuals/components/VisualAidFactory'
import {
  getVisualValidationAlignmentReport,
  getVisualRegistryIntegrityReport,
} from './visualRegistryIntegrity'
interface GovernanceVisualRule {
  stimulus?: { default?: string | null; required?: boolean }
  support?: { default?: string | null; required?: boolean }
}
interface QuestionSourceRef {
  file: string
  questionId: string
}
interface ResolvedAidCoverageItem {
  file: string
  questionId: string
  familyId: string
  rawStimulusCount: number
  rawSupportCount: number
  resolvedStimulusCount: number
  resolvedSupportCount: number
  reasons: string[]
}
export interface TaxonomyGovernanceReport {
  generatedAt: string
  taxonomyFamilyIds: string[]
  governanceFamilyIds: string[]
  questionFamilyIds: string[]
  visualAidTypesUsedInQuestions: string[]
  visualAidTypesSupportedByFactory: string[]
  taxonomyTotalFamilies: number
  governanceFamilies: number
  questionFamilies: number
  inTaxonomyNotInGovernance: { id: string }[]
  inGovernanceNotInTaxonomy: { id: string }[]
  inTaxonomyNotInQuestions: { id: string }[]
  inQuestionsNotInTaxonomy: {
    id: string
    files: string[]
    questionIds: string[]
  }[]
  invalidGovernanceEntries: { id: string; errors: string[] }[]
  aidTypesUsedButNotSupported: { type: string; usedIn: QuestionSourceRef[] }[]
  aidTypesSupportedButUnused: string[]
  visualValidatorRegistryAlignment: {
    registryTypes: string[]
    schemaTypes: string[]
    missingSchemasForRegistryTypes: string[]
    orphanSchemaTypes: string[]
  }
  visualRegistryIntegrity: {
    duplicateImportNames: string[]
    duplicateRegistryKeys: string[]
    missingRegistryKeysFromVisualAidType: string[]
    missingRegistryKeysFromFactoryTypes: string[]
    extraRegistryKeysOutsideVisualAidType: string[]
    hasIntegrityIssues: boolean
  }
  questionsWithRawAidsPct: number
  questionsWithResolvedAidsPct: number
  topZeroRawButNonZeroResolved: ResolvedAidCoverageItem[]
  topRequiredButStillZeroResolved: ResolvedAidCoverageItem[]
  possibleTypos: { badId: string; suggestedId: string; reason: string }[]
  legacyStatus: 'safe-to-delete' | 'mixed' | 'unknown'
  legacyStatusReasons: string[]
}
function toSet(values: string[]): Set<string> {
  return new Set(values.filter(Boolean))
}
function editDistance(a: string, b: string): number {
  const dp = Array.from({ length: a.length + 1 }, () =>
    Array(b.length + 1).fill(0),
  )
  for (let i = 0; i <= a.length; i++) dp[i][0] = i
  for (let j = 0; j <= b.length; j++) dp[0][j] = j
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost,
      )
    }
  }
  return dp[a.length][b.length]
}
export async function buildTaxonomyGovernanceReport(): Promise<TaxonomyGovernanceReport> {
  const runtimeQuestions = getAllQuestionsWithSourcesForDiagnostics()
  const taxonomyFamilyIds = TAXONOMY.map((f) => f.familyId).sort()
  const governanceFamilies = (visualGovernanceMap as any).families ?? {}
  const governanceFamilyIds = Object.keys(governanceFamilies).sort()
  const questionFamilySet = new Set<string>()
  const questionFamilyRefs = new Map<
    string,
    { files: Set<string>; questionIds: Set<string> }
  >()
  const usedTypeRefs = new Map<string, QuestionSourceRef[]>()
  const resolvedItems: ResolvedAidCoverageItem[] = []
  runtimeQuestions.forEach((question) => {
    questionFamilySet.add(question.familyId)
    if (!questionFamilyRefs.has(question.familyId)) {
      questionFamilyRefs.set(question.familyId, {
        files: new Set(),
        questionIds: new Set(),
      })
    }
    const refs = questionFamilyRefs.get(question.familyId)!
    refs.files.add(question.__sourceFile)
    refs.questionIds.add(question.questionId)
    const rawStimulus = question.stimulusAids ?? []
    const rawSupport = question.supportAids ?? []
    const rule = governanceFamilies[question.familyId] as
      | GovernanceVisualRule
      | undefined
    const resolvedStimulusCount =
      rawStimulus.length ||
      (rule?.stimulus?.required && rule.stimulus.default ? 1 : 0)
    const resolvedSupportCount =
      rawSupport.length ||
      (rule?.support?.required && rule.support.default ? 1 : 0)
    const reasons: string[] = []
    if (rawStimulus.length === 0 && resolvedStimulusCount > 0)
      reasons.push('Applied governance stimulus.default')
    if (rawSupport.length === 0 && resolvedSupportCount > 0)
      reasons.push('Applied governance support.default')
    if (
      rawStimulus.length === 0 &&
      rule?.stimulus?.required &&
      !rule.stimulus.default
    )
      reasons.push('Stimulus required but governance default missing')
    if (
      rawSupport.length === 0 &&
      rule?.support?.required &&
      !rule.support.default
    )
      reasons.push('Support required but governance default missing')
    resolvedItems.push({
      file: question.__sourceFile,
      questionId: question.questionId,
      familyId: question.familyId,
      rawStimulusCount: rawStimulus.length,
      rawSupportCount: rawSupport.length,
      resolvedStimulusCount,
      resolvedSupportCount,
      reasons,
    })
    ;[...rawStimulus, ...rawSupport].forEach((aid) => {
      if (!aid?.type) return
      if (!usedTypeRefs.has(aid.type)) usedTypeRefs.set(aid.type, [])
      usedTypeRefs
        .get(aid.type)!
        .push({ file: question.__sourceFile, questionId: question.questionId })
    })
  })
  const taxonomySet = toSet(taxonomyFamilyIds)
  const governanceSet = toSet(governanceFamilyIds)
  const taxonomyMissingInGovernance = taxonomyFamilyIds.filter(
    (id) => !governanceSet.has(id),
  )
  const governanceOrphans = governanceFamilyIds.filter(
    (id) => !taxonomySet.has(id),
  )
  if (
    taxonomyFamilyIds.length !== governanceFamilyIds.length ||
    taxonomyMissingInGovernance.length > 0 ||
    governanceOrphans.length > 0
  ) {
    throw new Error(
      [
        '[TaxonomyGovernanceMismatch] TAXONOMY and VISUAL_GOVERNANCE_MAP are out of sync.',
        `taxonomyCount=${taxonomyFamilyIds.length}`,
        `governanceCount=${governanceFamilyIds.length}`,
        `missingInGovernance=${taxonomyMissingInGovernance.join(',') || 'none'}`,
        `orphanedInGovernance=${governanceOrphans.join(',') || 'none'}`,
      ].join(' '),
    )
  }
  const questionFamilyIds = Array.from(questionFamilySet).sort()
  const questionSet = toSet(questionFamilyIds)
  const inQuestionsNotInTaxonomy = questionFamilyIds
    .filter((id) => !taxonomySet.has(id))
    .map((id) => {
      const refs = questionFamilyRefs.get(id)!
      return {
        id,
        files: Array.from(refs.files).sort(),
        questionIds: Array.from(refs.questionIds).sort(),
      }
    })
  const invalidGovernanceEntries: { id: string; errors: string[] }[] = []
  governanceFamilyIds.forEach((id) => {
    const familyRule = governanceFamilies[id] as GovernanceVisualRule
    const errors: string[] = []
    if (!familyRule) errors.push('Missing governance entry')
    if (familyRule?.stimulus?.required && !familyRule?.stimulus?.default)
      errors.push('stimulus.required=true but stimulus.default is missing')
    if (familyRule?.support?.required && !familyRule?.support?.default)
      errors.push('support.required=true but support.default is missing')
    if (errors.length) invalidGovernanceEntries.push({ id, errors })
  })
  const visualAidTypesUsedInQuestions = Array.from(usedTypeRefs.keys()).sort()
  const visualAidTypesSupportedByFactory = Array.from(
    VISUAL_AID_FACTORY_SUPPORTED_TYPES,
  ).sort()
  const supportedSet = toSet(visualAidTypesSupportedByFactory)
  const usedSet = toSet(visualAidTypesUsedInQuestions)
  const aidTypesUsedButNotSupported = visualAidTypesUsedInQuestions
    .filter((type) => !supportedSet.has(type))
    .map((type) => ({ type, usedIn: usedTypeRefs.get(type) ?? [] }))
  const aidTypesSupportedButUnused = visualAidTypesSupportedByFactory.filter(
    (type) => !usedSet.has(type),
  )
  const visualValidatorRegistryAlignment = getVisualValidationAlignmentReport()
  const visualRegistryIntegrity = getVisualRegistryIntegrityReport()
  const totalQuestions = runtimeQuestions.length || 1
  const rawWithAids = resolvedItems.filter(
    (q) => q.rawStimulusCount + q.rawSupportCount > 0,
  ).length
  const resolvedWithAids = resolvedItems.filter(
    (q) => q.resolvedStimulusCount + q.resolvedSupportCount > 0,
  ).length
  const topZeroRawButNonZeroResolved = resolvedItems
    .filter(
      (q) =>
        q.rawStimulusCount + q.rawSupportCount === 0 &&
        q.resolvedStimulusCount + q.resolvedSupportCount > 0,
    )
    .slice(0, 50)
  const topRequiredButStillZeroResolved = resolvedItems
    .filter((q) => {
      const rule = governanceFamilies[q.familyId] as
        | GovernanceVisualRule
        | undefined
      const required = Boolean(
        rule?.stimulus?.required || rule?.support?.required,
      )
      return required && q.resolvedStimulusCount + q.resolvedSupportCount === 0
    })
    .slice(0, 50)
  const possibleTypos = inQuestionsNotInTaxonomy.slice(0, 100).map(({ id }) => {
    const sortedCandidates = taxonomyFamilyIds
      .map((candidate) => ({
        candidate,
        distance: editDistance(id, candidate),
      }))
      .sort((a, b) => a.distance - b.distance)
    const best = sortedCandidates[0]
    return {
      badId: id,
      suggestedId: best?.candidate ?? '',
      reason: best
        ? `Levenshtein distance ${best.distance}`
        : 'No taxonomy candidates',
    }
  })
  return {
    generatedAt: new Date().toISOString(),
    taxonomyFamilyIds,
    governanceFamilyIds,
    questionFamilyIds,
    visualAidTypesUsedInQuestions,
    visualAidTypesSupportedByFactory,
    taxonomyTotalFamilies: taxonomyFamilyIds.length,
    governanceFamilies: governanceFamilyIds.length,
    questionFamilies: questionFamilyIds.length,
    inTaxonomyNotInGovernance: taxonomyMissingInGovernance.map((id) => ({
      id,
    })),
    inGovernanceNotInTaxonomy: governanceOrphans.map((id) => ({ id })),
    inTaxonomyNotInQuestions: taxonomyFamilyIds
      .filter((id) => !questionSet.has(id))
      .map((id) => ({ id })),
    inQuestionsNotInTaxonomy,
    invalidGovernanceEntries,
    aidTypesUsedButNotSupported,
    aidTypesSupportedButUnused,
    visualValidatorRegistryAlignment,
    visualRegistryIntegrity,
    questionsWithRawAidsPct:
      Math.round((rawWithAids / totalQuestions) * 10000) / 100,
    questionsWithResolvedAidsPct:
      Math.round((resolvedWithAids / totalQuestions) * 10000) / 100,
    topZeroRawButNonZeroResolved,
    topRequiredButStillZeroResolved,
    possibleTypos,
    legacyStatus: 'unknown',
    legacyStatusReasons: [
      'Static code scan unavailable without glob/raw import',
    ],
  }
}
